import { z } from "zod";
import { RequiredFirestoreTimestampSchema } from "./common.schema";

/**
 * Organization library document schema (without id)
 * Path: organizations/{orgId}/library/{docId}
 *
 * Read direct-from-Firestore by any org member (`isOrgMember` in the rules);
 * written only by core, because the record and the GCS object have to move
 * together. See ADR-0006 and ADR-0003.
 */

/**
 * Which reader gets at this file's content.
 *
 * `text` and `image` are readable as soon as they land. Only a `document`
 * (pdf, pptx) needs an extraction pass, because no agent accepts a native
 * document — the reason the whole kind split exists.
 */
export const LibraryDocumentKindSchema = z.enum(["text", "image", "document"]);
export type LibraryDocumentKind = z.infer<typeof LibraryDocumentKindSchema>;

/**
 * `extracting` and `failed` apply only to `document` kinds. A `failed`
 * document is still downloadable — a scanned PDF is a perfectly good file
 * that simply has no text layer for an agent to read.
 */
export const LibraryDocumentStatusSchema = z.enum([
  "pending",
  "extracting",
  "ready",
  "failed",
]);
export type LibraryDocumentStatus = z.infer<
  typeof LibraryDocumentStatusSchema
>;

export const LibraryDocumentDocSchema = z.object({
  // The original name as the member chose it. The object path carries a
  // sanitized form, so never rebuild one from the other.
  filename: z.string(),
  content_type: z.string(),
  kind: LibraryDocumentKindSchema,
  gcs_path: z.string(),
  status: LibraryDocumentStatusSchema,
  uploaded_by: z.string(),
  uploaded_at: RequiredFirestoreTimestampSchema,
  // Absent until finalize stats the object — it is read off the upload rather
  // than trusted from the client, so it does not exist until the bytes land.
  size_bytes: z.number().nullable().optional(),
});
export type LibraryDocumentDoc = z.infer<typeof LibraryDocumentDocSchema>;

export const LibraryDocumentSchema = LibraryDocumentDocSchema.extend({
  id: z.string(),
});
export type LibraryDocument = z.infer<typeof LibraryDocumentSchema>;

export const parseLibraryDocument = (data: unknown): LibraryDocument =>
  LibraryDocumentSchema.parse(data);
export const safeLibraryDocumentParse = (data: unknown) =>
  LibraryDocumentSchema.safeParse(data);
